import { useSyncExternalStore } from "react";
import { DataGridView, DataTable, IconButton, RotateCwIcon } from "@carbon6/c6-frontend-design";
import { FilterResultCount } from "@/shared/ui/filter";
import { ListToolbar } from "@/shared/ui/ListToolbar";
import { ViewModeToggle, type ViewMode } from "@/shared/ui/ViewModeToggle";
import type { DatasetFilters } from "../../feature/datasetFilters";
import { formatEmptyMessage } from "../../feature/datasetFormatters";
import { datasetColumns, datasetInitialSort } from "../datasetColumns";
import type { DatasetTableSource } from "../DatasetTableSource";
import { DatasetCard } from "./DatasetCard";
import { ListPagination } from "./ListPagination";
import styles from "./DatasetListBody.module.css";

type DatasetListBodyProps = {
  source: DatasetTableSource;
  filters: DatasetFilters;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onRefresh: () => void;
};

// 카드 그리드 셀 폭. 한 줄에 2열(셀당 ~1/2, gap 1개 보정)로 떨어지며, 폭이 좁아지면 floor 값(20rem)까지
// 줄다 그 아래에선 1열로 내려간다.
const CARD_CELL_WIDTH = "clamp(20rem, calc((100% - var(--app-spacing-large)) / 2), 100%)";

const LOADING_MESSAGE = "Dataset 목록을 불러오고 있습니다.";

/**
 * 목록 본문 — 도구 띠 + 카드 그리드 또는 표.
 *
 * 두 보기는 같은 소스를 읽는다(외부 스토어라 `useSyncExternalStore`). 페이지 클래스가 인스턴스를
 * 소유하고 이 컴포넌트는 그 스냅샷을 그리기만 한다. 보기 전환은 목록 도구(띠 우측)다(규칙 §13).
 */
export function DatasetListBody({
  filters,
  onRefresh,
  onViewModeChange,
  source,
  viewMode,
}: DatasetListBodyProps) {
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const loading = snapshot.loadState === "loading";
  const emptyMessage = formatEmptyMessage(filters);

  return (
    <div className={styles.body}>
      <ListToolbar
        actions={
          <>
            <IconButton
              disabled={loading}
              size="sm"
              text={loading ? "불러오는 중" : "새로고침"}
              variant="surface"
              onClick={onRefresh}
            >
              <RotateCwIcon />
            </IconButton>
            <ViewModeToggle
              label="Dataset 보기 방식"
              size="sm"
              value={viewMode}
              onChange={onViewModeChange}
            />
          </>
        }
        ariaLabel="Dataset 목록 도구"
        summary={<FilterResultCount count={snapshot.total} unitLabel="개" />}
      />
      {snapshot.errorMessage === null ? null : (
        <p className={styles.error} role="alert">
          {snapshot.errorMessage}
        </p>
      )}
      {viewMode === "grid" ? (
        <DataGridView
          ariaLabel="Dataset 목록"
          cellHeight="auto"
          cellWidth={CARD_CELL_WIDTH}
          emptyMessage={emptyMessage}
          loadingMessage={LOADING_MESSAGE}
          renderItem={(row) => <DatasetCard row={row} />}
          scrollable
          source={source}
        />
      ) : (
        <DataTable
          appearance="framed"
          ariaLabel="Dataset 목록"
          columns={datasetColumns}
          emptyMessage={emptyMessage}
          initialSort={datasetInitialSort}
          loadingMessage={LOADING_MESSAGE}
          minWidth="900px"
          scrollable
          source={source}
        />
      )}
      <ListPagination source={source} />
    </div>
  );
}
